import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Plus, Trash2, KeyRound, Loader2, UserCog } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  admin: "Administrador",
  gestora: "Gestor",
  indicador: "Indicador",
  socio: "Sócio",
};

const ROLE_BADGE = {
  admin: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  gestora: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  indicador: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  socio: "bg-purple-500/15 text-purple-300 border-purple-500/40",
};

export default function UsuariosPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [socios, setSocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [resetModal, setResetModal] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    role: "gestora",
    name: "",
    email: "",
    password: "",
    indicator_id: "",
    socio_id: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [u, i, s] = await Promise.all([
        api.get("/users"),
        api.get("/indicators").catch(() => ({ data: [] })),
        api.get("/socios").catch(() => ({ data: [] })),
      ]);
      setUsers(u.data);
      setIndicators(i.data || []);
      setSocios(s.data || []);
    } finally { setLoading(false); }
  };

  // Indicators/Socios that don't have a linked user yet
  const linkedIndIds = useMemo(() => new Set(users.filter(u => u.role === "indicador").map(u => u.indicator_id)), [users]);
  const linkedSocioIds = useMemo(() => new Set(users.filter(u => u.role === "socio").map(u => u.socio_id)), [users]);
  const indicatorsAvailable = useMemo(() => indicators.filter(i => !linkedIndIds.has(i.id) && i.email), [indicators, linkedIndIds]);
  const sociosAvailable = useMemo(() => socios.filter(s => !linkedSocioIds.has(s.id) && s.email), [socios, linkedSocioIds]);

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ role: "gestora", name: "", email: "", password: "", indicator_id: "", socio_id: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (form.role === "admin" || form.role === "gestora") {
        if (form.password.length < 6) { toast.error("Senha mínima de 6 caracteres"); setSaving(false); return; }
        await api.post("/users", {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          role: form.role,
          indicator_id: null,
        });
      } else if (form.role === "indicador") {
        if (!form.indicator_id) { toast.error("Selecione um indicador"); setSaving(false); return; }
        if (form.password.length < 6) { toast.error("Senha mínima de 6 caracteres"); setSaving(false); return; }
        await api.post(`/indicators/${form.indicator_id}/login`, { password: form.password });
      } else if (form.role === "socio") {
        if (!form.socio_id) { toast.error("Selecione um sócio"); setSaving(false); return; }
        if (form.password.length < 6) { toast.error("Senha mínima de 6 caracteres"); setSaving(false); return; }
        await api.post(`/socios/${form.socio_id}/login`, { password: form.password });
      }
      toast.success("Usuário criado");
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao criar usuário");
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (u.id === me?.id) { toast.error("Você não pode excluir o próprio usuário"); return; }
    if (!window.confirm(`Excluir o acesso de "${u.name || u.email}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuário excluído");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao excluir");
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (resetPwd.length < 6) { toast.error("Senha mínima de 6 caracteres"); return; }
    setSaving(true);
    try {
      await api.patch(`/users/${resetModal.id}/password`, { password: resetPwd });
      toast.success("Senha atualizada");
      setResetModal(null); setResetPwd("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao redefinir");
    } finally { setSaving(false); }
  };

  const linkedLabel = (u) => {
    if (u.role === "indicador" && u.indicator_id) {
      const ind = indicators.find(i => i.id === u.indicator_id);
      return ind?.name || "—";
    }
    if (u.role === "socio" && u.socio_id) {
      const so = socios.find(s => s.id === u.socio_id);
      return so?.name || "—";
    }
    return "—";
  };

  return (
    <Layout>
      <div className="flex items-end justify-between mb-7 gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Acesso</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">Usuários</h1>
          <p className="text-[#A3A3A3] text-[13px] sm:text-sm mt-1">Gerencie os acessos: Administrador, Gestor, Indicador e Sócio.</p>
        </div>
        <button onClick={()=>{ resetForm(); setOpen(true); }} className="fc-btn-gold flex items-center gap-2" data-testid="btn-new-user">
          <Plus size={15}/> Novo usuário
        </button>
      </div>

      <div className="fc-card overflow-hidden">
        <table className="hidden md:table w-full text-sm" data-testid="users-table">
          <thead className="bg-[#0f0f0f] border-b border-[#1f1f1f] text-[11px] uppercase tracking-wider text-[#737373]">
            <tr>
              <th className="text-left px-5 py-3">Nome</th>
              <th className="text-left px-5 py-3">E-mail</th>
              <th className="text-left px-5 py-3">Perfil</th>
              <th className="text-left px-5 py-3">Vínculo</th>
              <th className="text-right px-5 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="text-center text-[#737373] py-10"><Loader2 className="inline animate-spin mr-2" size={14}/>Carregando...</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={5} className="text-center text-[#737373] py-14">Nenhum usuário cadastrado.</td></tr>
            )}
            {!loading && users.map(u => (
              <tr key={u.id} className="border-b border-[#161616] hover:bg-[#111] transition-colors" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3 font-medium">{u.name || "—"} {u.id === me?.id && <span className="ml-1 text-[10px] uppercase tracking-wider text-[#D4AF37]">(você)</span>}</td>
                <td className="px-5 py-3 text-[#A3A3A3]">{u.email}</td>
                <td className="px-5 py-3">
                  <span className={`badge-base border ${ROLE_BADGE[u.role] || "bg-neutral-800 text-neutral-400 border-neutral-700"}`}>{ROLE_LABELS[u.role] || u.role}</span>
                </td>
                <td className="px-5 py-3 text-[#A3A3A3]">{linkedLabel(u)}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-3">
                    <button onClick={()=>{ setResetModal(u); setResetPwd(""); }} className="text-[#D4AF37]/80 hover:text-[#D4AF37] text-xs flex items-center gap-1" data-testid={`reset-pwd-${u.id}`}>
                      <KeyRound size={13}/> Senha
                    </button>
                    {u.id !== me?.id && (
                      <button onClick={()=>remove(u)} className="text-red-400/70 hover:text-red-300 text-xs flex items-center gap-1" data-testid={`delete-user-${u.id}`}>
                        <Trash2 size={13}/> Excluir
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Cards mobile */}
        <div className="md:hidden divide-y divide-[#1f1f1f]" data-testid="users-cards">
          {loading && <div className="py-10 text-center text-[#737373]"><Loader2 className="inline animate-spin mr-2" size={14}/>Carregando...</div>}
          {!loading && users.length === 0 && <div className="py-12 text-center text-[#737373] text-sm">Nenhum usuário.</div>}
          {!loading && users.map(u => (
            <div key={u.id} className="p-4" data-testid={`user-card-${u.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[14.5px] truncate">
                    {u.name || "—"} {u.id === me?.id && <span className="ml-1 text-[10px] uppercase tracking-wider text-[#D4AF37]">(você)</span>}
                  </div>
                  <div className="text-[12px] text-[#A3A3A3] mt-0.5 truncate">{u.email}</div>
                  <div className="text-[11.5px] text-[#737373] mt-0.5 truncate">{linkedLabel(u)}</div>
                </div>
                <span className={`badge-base border ${ROLE_BADGE[u.role] || "bg-neutral-800 text-neutral-400 border-neutral-700"} flex-shrink-0`}>{ROLE_LABELS[u.role] || u.role}</span>
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#1f1f1f]">
                <button onClick={()=>{ setResetModal(u); setResetPwd(""); }} className="text-[#D4AF37] text-[12.5px] flex items-center gap-1" data-testid={`reset-pwd-mobile-${u.id}`}>
                  <KeyRound size={13}/> Senha
                </button>
                {u.id !== me?.id && (
                  <button onClick={()=>remove(u)} className="text-red-400/80 text-[12.5px] flex items-center gap-1 ml-auto" data-testid={`delete-user-mobile-${u.id}`}>
                    <Trash2 size={13}/> Excluir
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-[12px] text-[#737373] mt-4 leading-relaxed">
        <UserCog size={12} className="inline mr-1 -mt-0.5"/>
        <strong>Indicadores</strong> e <strong>Sócios</strong> precisam ser cadastrados primeiro nas suas respectivas seções antes de receberem acesso. O e-mail informado no cadastro do parceiro será o login.
      </p>

      {/* Create modal */}
      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={()=>setOpen(false)}>
          <form onSubmit={submit} className="fc-card w-full max-w-md p-6" onClick={e=>e.stopPropagation()} data-testid="user-form-modal">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Novo</div>
            <h3 className="font-display text-2xl font-bold mt-1">Usuário</h3>
            <div className="space-y-4 mt-5">
              <div>
                <label className="fc-label">Perfil</label>
                <select className="fc-input" value={form.role} onChange={e=>setForm({...form, role: e.target.value, name:"", email:"", indicator_id:"", socio_id:""})} data-testid="user-role">
                  <option value="admin">Administrador</option>
                  <option value="gestora">Gestor</option>
                  <option value="indicador">Indicador</option>
                  <option value="socio">Sócio</option>
                </select>
              </div>

              {(form.role === "admin" || form.role === "gestora") && (
                <>
                  <div>
                    <label className="fc-label">Nome</label>
                    <input required className="fc-input" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="user-name"/>
                  </div>
                  <div>
                    <label className="fc-label">E-mail</label>
                    <input required type="email" className="fc-input" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} data-testid="user-email"/>
                  </div>
                </>
              )}

              {form.role === "indicador" && (
                <div>
                  <label className="fc-label">Indicador</label>
                  <select required className="fc-input" value={form.indicator_id} onChange={e=>setForm({...form, indicator_id: e.target.value})} data-testid="user-indicator">
                    <option value="">Selecione um indicador...</option>
                    {indicatorsAvailable.map(i => (
                      <option key={i.id} value={i.id}>{i.name} ({i.email})</option>
                    ))}
                  </select>
                  {indicatorsAvailable.length === 0 && (
                    <p className="text-[11px] text-amber-400/80 mt-1.5">Nenhum indicador disponível sem acesso. Cadastre um indicador com e-mail em &quot;Indicadores&quot;.</p>
                  )}
                </div>
              )}

              {form.role === "socio" && (
                <div>
                  <label className="fc-label">Sócio</label>
                  <select required className="fc-input" value={form.socio_id} onChange={e=>setForm({...form, socio_id: e.target.value})} data-testid="user-socio">
                    <option value="">Selecione um sócio...</option>
                    {sociosAvailable.map(s => (
                      <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
                    ))}
                  </select>
                  {sociosAvailable.length === 0 && (
                    <p className="text-[11px] text-amber-400/80 mt-1.5">Nenhum sócio disponível sem acesso. Cadastre um sócio com e-mail em &quot;Sócios&quot;.</p>
                  )}
                </div>
              )}

              <div>
                <label className="fc-label">Senha (mín. 6 caracteres)</label>
                <input required type="password" minLength={6} className="fc-input" value={form.password} onChange={e=>setForm({...form, password: e.target.value})} data-testid="user-password"/>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={()=>setOpen(false)} className="fc-btn-ghost">Cancelar</button>
              <button type="submit" disabled={saving} className="fc-btn-gold flex items-center gap-2" data-testid="user-save">
                {saving && <Loader2 size={15} className="animate-spin"/>} Criar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reset password modal */}
      {resetModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={()=>setResetModal(null)}>
          <form onSubmit={submitReset} className="fc-card w-full max-w-md p-6" onClick={e=>e.stopPropagation()} data-testid="user-reset-modal">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Redefinir senha</div>
            <h3 className="font-display text-2xl font-bold mt-1">{resetModal.name || resetModal.email}</h3>
            <p className="text-[12.5px] text-[#A3A3A3] mt-2">E-mail: <span className="text-white">{resetModal.email}</span></p>
            <div className="mt-5">
              <label className="fc-label">Nova senha (mín. 6 caracteres)</label>
              <input required type="password" minLength={6} className="fc-input" value={resetPwd} onChange={e=>setResetPwd(e.target.value)} data-testid="user-new-password"/>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={()=>setResetModal(null)} className="fc-btn-ghost">Cancelar</button>
              <button type="submit" disabled={saving} className="fc-btn-gold flex items-center gap-2" data-testid="user-reset-save">
                {saving && <Loader2 size={15} className="animate-spin"/>} Salvar
              </button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
